import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import {
  DriverViewModel,
  DriverNote,
  DriverNoteAttachment,
  DriverNoteUpdate,
  DriverNoteEmail,
} from "@app/shared/models/drivers/DriverViewModel.model";
import { MessageService } from "primeng/api";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { authenticationService } from "@app/login/auth.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { clientService } from "@app/client/shared/client.service";
import { getDriverDisplayName, getLastFirstName } from "@app/shared/utils/driver-name.util";
import { toDateOnlyString } from "@app/shared/utils/date-only.util";
import { ConfigService } from "@app/utilities/config/shared/config.service";
import { MaskService } from "@app/shared/services/mask/mask.service";
import { FileUploadService } from "@app/shared/services/files/fileUpload.service";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

@Component({
  selector: "app-driver-note-form-modal",
  templateUrl: "./driver-note-form-modal.component.html",
  styleUrls: ["./driver-note-form-modal.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverNoteFormModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() note: Partial<DriverNote> = {};
  @Input() driver: DriverViewModel | null = null;
  @Input() readOnly: boolean = false;
  @Input() hideEmailButtons: boolean = false;
  dateInputMask: any;

  /** Driver name to display: custom display name when set, otherwise "First Last". */
  get driverDisplayName(): string {
    return getDriverDisplayName(this.driver);
  }

  /**
   * Team (co-driver) names of the note's driver, or of the driver picked from the modal's own
   * driver dropdown (whose full record is looked up when the dropdown list has no team names).
   */
  get driverTeamMembers(): string[] {
    const members = this.driver ? this.driver.teamMembers : this.driverSelectTeamMembers;
    return (members ?? []).map((name) => (name ?? "").trim()).filter((name) => !!name);
  }

  /**
   * The team names as a single comma-separated string for display, last name first to match the
   * driver dropdown beside it. The email name above keeps the "First Last" form the customers see.
   */
  get driverTeamMembersText(): string {
    return this.driverTeamMembers.map(getLastFirstName).join(", ");
  }

  /**
   * Driver name used in emails: the note's driver plus the other drivers on the same team
   * (the co-driver), e.g. "John Smith & Jane Doe", so the customer sees the whole team.
   */
  get driverEmailName(): string {
    const names = [getDriverDisplayName(this.driver), ...this.driverTeamMembers]
      .map((name) => (name ?? "").trim())
      .filter((name) => !!name);
    return [...new Set(names)].join(" & ");
  }

  // Dropdown data
  @Input() tractors: any[] = [];
  @Input() trailers: any[] = [];
  @Input() drivers: any[] = [];
  @Input() clients: any[] = [];
  @Input() allClients: any[] = [];
  @Input() products: any[] = [];
  @Input() states: any[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<DriverNote>();
  @Output() closed = new EventEmitter<void>();
  /**
   * Emits the scheduling team assignment (client / bill-to) resolved for the driver
   * picked in this modal's driver dropdown, so consumers with filtered client lists
   * can keep those options available.
   */
  @Output() assignmentResolved = new EventEmitter<{ clientId?: string; billToId?: string }>();

  noteTypes: string[] = [
    "General",
    "Driver Release",
    "Delays",
    "Breakdown & follow up",
    "Accident",
    "Incident",
    "Complaint",
  ];

  truckTrailerIssueOptions: { label: string; value: string }[] = [
    { label: "Truck", value: "Truck" },
    { label: "Trailer", value: "Trailer" },
    { label: "Both", value: "Both" },
  ];

  /**
   * Accident/incident time zone options. Same time zones the location form offers, stored as the
   * readable label (this is free-text-ish note data, unlike a location's IANA id).
   */
  timeZoneOptions: string[] = [
    "Eastern Time (ET)",
    "Central Time (CT)",
    "Mountain Time (MT)",
    "Mountain Time - Arizona (MST)",
    "Pacific Time (PT)",
    "Alaska Time (AKT)",
    "Hawaii Time (HST)",
    "Atlantic Time (AT)",
    "Newfoundland Time (NT)",
    "Saskatchewan Time (CST)",
    "Central Time - Mexico (CST)",
    "Mountain Time - Mexico (MST)",
    "Pacific Time - Mexico (PST)",
  ];

  editNote: Partial<DriverNote> = {};
  filteredBillTos: any[] = [];
  attemptedSubmit: boolean = false;
  previousNoteType: string = "";
  /** UI-only: when true, ETA is unknown (pending diagnostics) and not required. */
  etaUnknown: boolean = false;
  /** Customer assignment of the driver picked from the modal's driver dropdown. */
  private driverSelectAssignment: { clientId?: string; billToId?: string } | null = null;
  /** Team (co-driver) names of the driver picked from the modal's driver dropdown. */
  private driverSelectTeamMembers: string[] = [];

  /** Client from the driver's scheduling team assignment. */
  private get assignedClientId(): string | undefined {
    return this.driver?.schedulingTeamClientId ?? this.driverSelectAssignment?.clientId;
  }

  /** Bill-to from the driver's scheduling team assignment. */
  private get assignedBillToId(): string | undefined {
    return this.driver?.billToClientId ?? this.driverSelectAssignment?.billToId;
  }

  // Weather autocomplete
  allWeatherValues: string[] = [];
  filteredWeatherValues: string[] = [];

  // Email preview states
  showEmailPreview: boolean = false;
  currentEmailType: "toSafety" | "formToDriver" | "toClient" | "toDriver" | null = null;
  emailPreview: {
    to: string;
    ccEmails: string;
    subject: string;
    body: string;
  } = { to: "", ccEmails: "", subject: "", body: "" };
  /** Read-only HTML for the quoted previous email, rendered (and sent) below the editable message. */
  emailQuotedHtml: string = "";
  emailAttachments: File[] = [];
  emailValidationErrors: { to: string; cc: string } = { to: "", cc: "" };

  // Email states (for button indicators in footer)
  emailingToSafety: boolean = false;
  emailingFormToDriver: boolean = false;
  emailingToClient: boolean = false;
  emailingToDriver: boolean = false;

  // ── Post-create email prompt ────────────────────────────────────────────────
  /** True while the "would you like to email..." prompt is open after a note was created. */
  showEmailPrompt: boolean = false;
  /** Note type the post-create email prompt is being shown for. */
  promptNoteType: string = "";

  /**
   * CC address for note emails (SendGrid.DriverNotesCcEmail) - omahadispatch@lgttransport.com in
   * production. Every note email (including a driver release) is copied to it.
   */
  driverNotesCcEmail: string = "";
  /** One-shot load of the notes CC address, awaited before composing a note email. */
  private ccEmailPromise: Promise<void> | null = null;
  /** Sending mailbox used for note emails (shown in the quoted reply header). */
  driverNotesFromEmail: string = "";
  driverNotesFromName: string = "";
  /** Current user's email signature (from their user profile), appended to client-facing emails. */
  userEmailSignature: string = "";

  // ── Self-service signature editor (Review Email dialog) ────────────────────
  showSignatureEditor: boolean = false;
  signatureEditText: string = "";
  savingSignature: boolean = false;
  signatureSaveMessage: string = "";
  signatureSaveError: boolean = false;

  // ── Breakdown status updates ───────────────────────────────────────────────
  showAddUpdate: boolean = false;
  isSavingUpdate: boolean = false;
  updateAttemptedSubmit: boolean = false;
  addUpdate: { label?: string; internalNotes?: string; clientNotes?: string } = {};
  /** When set, the update form is editing this existing update instead of adding. */
  editingUpdateId: string | null = null;
  /** Set when the open "Review Email" dialog is for a breakdown update (vs the note). */
  private emailingUpdateId: string | null = null;
  /** Set once the email addresses / signature / weather list have been requested. */
  private emailConfigLoaded = false;
  /** Previously sent client emails for the open note, newest first (for reply-all quoting). */
  private noteEmailThread: DriverNoteEmail[] = [];
  /** The new (unquoted) body of the client email currently being composed. */
  private emailBaseBody: string = "";

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  // ── Complaint tagging ──────────────────────────────────────────────────────
  /** Active internal users that can be tagged on a complaint note. */
  taggableUsers: { id: string; name: string }[] = [];
  private taggableUsersLoaded = false;

  /** Loads the taggable internal users once, on demand (only complaint notes need them). */
  loadTaggableUsers(): void {
    if (this.taggableUsersLoaded) return;
    this.taggableUsersLoaded = true;

    this.driverService.getTaggableUsers().subscribe({
      next: (users) => {
        this.taggableUsers = users || [];
        this.cdr.markForCheck();
      },
      error: () => {
        this.taggableUsersLoaded = false;
      },
    });
  }

  /** True when a "Breakdown & follow up" note is set to Resolved but has no end date yet. */
  get resolvedBreakdownMissingEndDate(): boolean {
    return (
      this.isBreakdown(this.editNote.type) &&
      this.editNote.status === "Resolved" &&
      !this.editNote.endDate
    );
  }

  /** True when both dates are set and the end date falls before the start date. Same date is valid. */
  get endDateBeforeStartDate(): boolean {
    const start = toDateOnlyString(this.editNote.startDate);
    const end = toDateOnlyString(this.editNote.endDate);
    if (!start || !end) return false;
    return end < start;
  }

  constructor(
    private messageService: MessageService,
    private authService: authenticationService,
    private driverService: DriverService,
    private clientServiceRef: clientService,
    private configService: ConfigService,
    private maskService: MaskService,
    private cdr: ChangeDetectorRef,
    private featureFlags: FeatureFlagsService,
    private fileUploadService: FileUploadService,
    private sanitizer: DomSanitizer
  ) {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
  }

  // ── General note attachments ───────────────────────────────────────────────
  /** True while chosen attachments are being uploaded to blob storage. */
  attachmentUploading = false;
  /** True while the attachment preview dialog is open. */
  showAttachmentPreview = false;
  /** The attachment currently open in the preview dialog. */
  attachmentPreviewItem: DriverNoteAttachment | null = null;
  /** Sanitized URL the preview dialog's PDF frame points at (set when the preview opens). */
  attachmentPreviewUrl: SafeResourceUrl | null = null;
  /** Resolves when the in-flight attachment upload finishes, so a save can wait for it. */
  private pendingAttachmentUpload: Promise<void> | null = null;
  /** True once this note's attachments were added or removed, so the save knows to send them. */
  private attachmentsTouched = false;

  /** Extensions a note attachment may use; the server enforces the same list. */
  private static readonly attachmentExtensions = [
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".webp",
    ".bmp",
  ];
  private static readonly maxAttachmentBytes = 25 * 1024 * 1024;

  /** Attachments shown for the note being edited. */
  get attachments(): DriverNoteAttachment[] {
    return this.editNote?.attachments ?? [];
  }

  /** True when the note carries at least one attachment. */
  get hasAttachments(): boolean {
    return this.attachments.length > 0;
  }

  /** Inline URL of an attachment, rendered by the browser for PDFs and images. */
  attachmentHref(attachment: DriverNoteAttachment): string {
    return attachment?.name
      ? `/api/download/drivernotes/${encodeURIComponent(attachment.name)}`
      : "";
  }

  private attachmentKind(attachment: DriverNoteAttachment | null): "pdf" | "image" | "other" {
    if (!attachment) return "other";
    const contentType = (attachment.type || "").toLowerCase();
    if (contentType.includes("pdf")) return "pdf";
    if (contentType.startsWith("image/")) return "image";

    const name = attachment.originalName || attachment.name || "";
    const extension = name.slice(name.lastIndexOf(".")).toLowerCase();
    if (extension === ".pdf") return "pdf";
    if (DriverNoteFormModalComponent.attachmentExtensions.includes(extension)) return "image";
    return "other";
  }

  attachmentIsPdf(attachment: DriverNoteAttachment): boolean {
    return this.attachmentKind(attachment) === "pdf";
  }

  attachmentIsImage(attachment: DriverNoteAttachment): boolean {
    return this.attachmentKind(attachment) === "image";
  }

  /** Font Awesome icon for an attachment, based on its type. */
  attachmentIcon(attachment: DriverNoteAttachment): string {
    switch (this.attachmentKind(attachment)) {
      case "pdf":
        return "fa-file-pdf-o";
      case "image":
        return "fa-file-image-o";
      default:
        return "fa-paperclip";
    }
  }

  /** Uploads every chosen file and appends the results to the note's attachments. */
  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    if (files.length === 0) return;

    const accepted: File[] = [];
    for (const file of files) {
      const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!DriverNoteFormModalComponent.attachmentExtensions.includes(extension)) {
        this.messageService.add({
          severity: "error",
          summary: "Unsupported File",
          detail: `${file.name}: attach a PDF or an image.`,
        });
        continue;
      }
      if (file.size > DriverNoteFormModalComponent.maxAttachmentBytes) {
        this.messageService.add({
          severity: "error",
          summary: "File Too Large",
          detail: `${file.name}: attachments must be 25 MB or smaller.`,
        });
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;

    this.attachmentUploading = true;
    this.attachmentsTouched = true;
    const upload = new Promise<void>((resolve) => {
      this.fileUploadService.uploadFiles(accepted, "driverNote").subscribe({
        next: (results: any[]) => {
          this.attachmentUploading = false;
          this.pendingAttachmentUpload = null;
          const uploaded = (results || []).filter((r: any) => !!r?.name);
          if (uploaded.length === 0) {
            this.messageService.add({
              severity: "error",
              summary: "Upload Failed",
              detail: "The attachments could not be saved. Please try again.",
            });
            this.cdr.markForCheck();
            resolve();
            return;
          }
          const existing = this.editNote.attachments ?? [];
          this.editNote.attachments = [
            ...existing,
            ...uploaded.map((result: any, index: number) => ({
              name: result.name,
              originalName: result.originalName || accepted[index]?.name,
              type: result.type || accepted[index]?.type,
            })),
          ];
          this.cdr.markForCheck();
          resolve();
        },
        error: () => {
          this.attachmentUploading = false;
          this.pendingAttachmentUpload = null;
          this.messageService.add({
            severity: "error",
            summary: "Upload Failed",
            detail: "The attachments could not be saved. Please try again.",
          });
          this.cdr.markForCheck();
          resolve();
        },
      });
    });
    this.pendingAttachmentUpload = upload;
  }

  /**
   * Resolves once any attachment upload is finished, so a save never runs while a chosen file is
   * still on its way up (the row would then be saved without it).
   */
  private async waitForAttachmentUpload(): Promise<void> {
    while (this.pendingAttachmentUpload) {
      await this.pendingAttachmentUpload;
    }
  }

  /**
   * Puts the note's attachments on the object being saved. A new note always carries its list; an
   * existing note only sends one when this dialog actually knows the note's attachments, so a note
   * opened without them cannot clear them on save.
   */
  private resolveAttachmentPayload(): void {
    if (!this.editNote.id || this.attachmentsTouched) {
      this.editNote.attachments = this.attachments;
    } else {
      delete this.editNote.attachments;
    }
  }

  /** Removes one attachment from the note (persisted when the note is saved). */
  removeNoteAttachment(attachment: DriverNoteAttachment): void {
    this.editNote.attachments = this.attachments.filter((a) => a !== attachment);
    this.attachmentsTouched = true;
    this.cdr.markForCheck();
  }

  previewAttachment(attachment: DriverNoteAttachment): void {
    if (!attachment?.name) return;
    this.attachmentPreviewItem = attachment;
    this.attachmentPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.attachmentHref(attachment)
    );
    this.showAttachmentPreview = true;
    this.cdr.markForCheck();
  }

  closeAttachmentPreview(): void {
    this.showAttachmentPreview = false;
    this.attachmentPreviewItem = null;
    this.attachmentPreviewUrl = null;
    this.cdr.markForCheck();
  }

  /**
   * Loads the notes CC address once and resolves when it is known (blank when the config call
   * fails). Cached so a later note email can wait for it instead of composing with a blank CC.
   */
  private loadCcEmail(): Promise<void> {
    if (!this.ccEmailPromise) {
      this.ccEmailPromise = new Promise<void>((resolve) => {
        this.configService.getDriverNotesCcEmail().subscribe(
          (ccEmail: string) => {
            this.driverNotesCcEmail = ccEmail || "";
            this.cdr.markForCheck();
            resolve();
          },
          () => resolve()
        );
      });
    }
    return this.ccEmailPromise;
  }

  /**
   * Runs the callback once the notes CC is known, so a note email (a driver release included) is
   * never composed or sent without the configured CC.
   */
  private ensureCcEmail(done: () => void): void {
    if (this.driverNotesCcEmail) {
      done();
      return;
    }
    void this.loadCcEmail().then(done);
  }

  /**
   * Addresses, signature and the weather list are only needed once the dialog is open. Loading them
   * on first open (instead of in the constructor) keeps four requests off every page that hosts a
   * note modal until the user actually opens one.
   */
  private loadEmailConfig(): void {
    if (this.emailConfigLoaded) return;
    this.emailConfigLoaded = true;

    void this.loadCcEmail();
    this.configService.getDriverNotesFromEmail().subscribe(
      (from: { email: string; name: string }) => {
        this.driverNotesFromEmail = from?.email || "";
        this.driverNotesFromName = from?.name || "";
        this.cdr.markForCheck();
      },
      () => {}
    );
    this.configService.getCurrentUserEmailSignature().subscribe(
      (signature: string) => {
        this.userEmailSignature = signature;
        this.cdr.markForCheck();
      },
      () => {}
    );
    this.driverService.getWeatherSuggestions().subscribe((data: string[]) => {
      this.allWeatherValues = data || [];
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["visible"] && this.visible) {
      this.loadEmailConfig();
      this.showAttachmentPreview = false;
      this.attachmentPreviewItem = null;
      this.attachmentPreviewUrl = null;
      this.attachmentUploading = false;
      this.pendingAttachmentUpload = null;
      // Only notes whose attachments were actually loaded count as "known", so saving a note
      // opened from a summary (with no attachment list) cannot wipe the stored files.
      this.attachmentsTouched = Array.isArray(this.note?.attachments);
      this.editNote = { ...this.note, note: this.note.note ?? "" };
      this.attemptedSubmit = false;
      this.driverSelectAssignment = null;
      this.driverSelectTeamMembers = [];
      // Unknown ETA isn't stored as a flag; infer it from a null ETA on delay/breakdown notes.
      this.etaUnknown = this.isDelayOrBreakdown(this.note.type) && !this.editNote.eta;
      this.previousNoteType = this.note.type || "";
      if (this.editNote.followUpDate) {
        const d = new Date(this.editNote.followUpDate as any);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const yyyy = d.getUTCFullYear();
          (this.editNote as any).followUpDate = `${mm}/${dd}/${yyyy}`;
        }
      }
      // p-calendar requires a Date object; the server returns eta as an ISO string
      if (this.editNote.eta) {
        const d = new Date(this.editNote.eta as any);
        if (!isNaN(d.getTime())) {
          this.editNote.eta = d;
        }
      }
      // p-calendar requires a Date object; the server returns startDate as an ISO string
      if (this.editNote.startDate) {
        const d = new Date(this.editNote.startDate as any);
        if (!isNaN(d.getTime())) {
          this.editNote.startDate = d;
        }
      } else if (!this.editNote.id && this.isDelayOrBreakdown(this.editNote.type)) {
        this.editNote.startDate = new Date();
      }
      // A breakdown records which tractor / trailer is out of service, so default it from the
      // driver's current assignment (the dispatcher only changes it for a different unit).
      if (!this.editNote.id && this.isBreakdown(this.editNote.type) && this.driver) {
        this.editNote.tractorId = this.editNote.tractorId ?? this.driver.tractorId;
        this.editNote.trailerId = this.editNote.trailerId ?? this.driver.trailerId;
      }
      // p-calendar requires a Date object; the server returns endDate as an ISO string
      if (this.editNote.endDate) {
        const d = new Date(this.editNote.endDate as any);
        if (!isNaN(d.getTime())) {
          this.editNote.endDate = d;
        }
      }
      if (this.editNote.clientId) {
        this.onClientChange();
      } else if (this.isDelayOrBreakdown(this.editNote.type)) {
        // New delay/breakdown note for a known driver: default the client / bill-to
        // from their customer assignment so they don't have to be selected manually.
        this.applyCustomerAssignment();
      }
      this.sortUpdates();
      this.resetUpdateState();
      this.loadNoteUpdates();
      if (!this.readOnly && this.isComplaint(this.editNote.type)) {
        this.loadTaggableUsers();
      }
      this.cdr.markForCheck();
    } else if (this.visible && (changes["allClients"] || changes["clients"])) {
      // Dropdown source data (clients / bill-tos) arrived or changed after the
      // modal opened - repopulate dependent options such as bill-to.
      if (this.editNote?.clientId) {
        this.onClientChange();
      }
      this.cdr.markForCheck();
    } else if (this.visible && changes["driver"]) {
      // The driver (with their customer assignment) arrived after the modal opened.
      if (!this.editNote?.clientId && this.isDelayOrBreakdown(this.editNote?.type)) {
        this.applyCustomerAssignment();
      }
      // Same for a new breakdown's tractor / trailer, which come from the driver assignment.
      if (!this.editNote?.id && this.isBreakdown(this.editNote?.type) && this.driver) {
        this.editNote.tractorId = this.editNote.tractorId ?? this.driver.tractorId;
        this.editNote.trailerId = this.editNote.trailerId ?? this.driver.trailerId;
      }
      this.cdr.markForCheck();
    }
  }

  get dialogHeader(): string {
    if (this.readOnly) return "View Note";
    return this.editNote.id ? "Edit Note" : "Add Note";
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  /** When "Unknown ETA" is checked, clear any entered ETA so it isn't saved. */
  onEtaUnknownChange() {
    if (this.etaUnknown) {
      this.editNote.eta = undefined;
    }
  }

  async saveNote(): Promise<void> {
    this.attemptedSubmit = true;

    if (!this.editNote.type) {
      this.scrollToFirstError();
      return;
    }

    // A file that is still uploading is not part of the note yet; wait for it so the save
    // cannot drop the attachment the user just picked.
    await this.waitForAttachmentUpload();
    this.resolveAttachmentPayload();

    if (!this.editNote.driverId && !this.readOnly) {
      this.messageService.add({ severity: "warn", summary: "Driver is required" });
      this.scrollToFirstError();
      return;
    }

    if (this.isAccidentOrIncident(this.editNote.type)) {
      if (
        this.editNote.anyInjuries === undefined ||
        this.editNote.anyInjuries === null ||
        this.editNote.anotherVehicleInvolved === undefined ||
        this.editNote.anotherVehicleInvolved === null ||
        this.editNote.driverCitation === undefined ||
        this.editNote.driverCitation === null ||
        (this.editNote.anyInjuries === true && !this.hasInjuriesDetails) ||
        !this.editNote.tractorId ||
        !this.editNote.trailerId ||
        !this.editNote.loadedEmpty ||
        !this.editNote.productId ||
        !this.editNote.accidentDate ||
        !this.editNote.accidentTime ||
        !this.editNote.timeZone ||
        !this.editNote.accidentCity ||
        !this.editNote.accidentState ||
        !this.editNote.accidentLocation ||
        !this.editNote.weather ||
        !this.editNote.accidentDescription ||
        !this.editNote.damageDescription ||
        !this.editNote.assignedCustomer ||
        !this.editNote.photosToSafety ||
        (this.editNote.driverCitation && !this.editNote.citationDescription)
      ) {
        this.scrollToFirstError();
        return;
      }
    } else if (this.isDelayOrBreakdown(this.editNote.type)) {
      if (
        !this.editNote.clientId ||
        !this.editNote.billToId ||
        !this.editNote.loadedEmpty ||
        (!this.editNote.eta && !this.etaUnknown) ||
        !this.editNote.clientNotes ||
        (this.isBreakdown(this.editNote.type) && !this.editNote.roadCallNumber) ||
        (this.isBreakdown(this.editNote.type) && !this.editNote.truckTrailerIssue) ||
        this.resolvedBreakdownMissingEndDate ||
        this.endDateBeforeStartDate
      ) {
        this.scrollToFirstError();
        return;
      }
    } else if (this.isDriverRelease(this.editNote.type)) {
      if (
        !this.editNote.driverLocation ||
        !this.editNote.loadedEmpty ||
        this.editNote.requestTimeOff === undefined ||
        this.editNote.requestTimeOff === null ||
        !this.editNote.trailerDropoffLocation ||
        !this.editNote.nextAvailable
      ) {
        this.scrollToFirstError();
        return;
      }
    } else {
      if (!this.editNote.note) {
        this.scrollToFirstError();
        return;
      }
    }

    // Populate display names (only when the lookup succeeds - a host that passes no tractor /
    // trailer list must not blank out numbers that came from the server). A cleared selection
    // clears the stored number too, so the dashboard's Tractor / Trailer column (and its filter,
    // which match on the number) stay in step with the dropdowns above.
    if (this.editNote.tractorId) {
      const tractorNumber = this.tractorNumberForId(this.editNote.tractorId);
      if (tractorNumber) this.editNote.tractorNumber = tractorNumber;
    } else {
      this.editNote.tractorNumber = undefined;
    }
    if (this.editNote.trailerId) {
      const trailerNumber = this.trailerNumberForId(this.editNote.trailerId);
      if (trailerNumber) this.editNote.trailerNumber = trailerNumber;
    } else {
      this.editNote.trailerNumber = undefined;
    }
    if (this.editNote.clientId) {
      const client = this.clients.find((c: any) => c.id === this.editNote.clientId);
      this.editNote.clientName = client?.name;
    }
    if (this.editNote.billToId) {
      const billTo = this.filteredBillTos.find((c: any) => c.id === this.editNote.billToId);
      this.editNote.billToName = billTo?.name || billTo?.companyName;
    }
    if (this.editNote.productId) {
      const product = this.products.find((p: any) => p.id === this.editNote.productId);
      this.editNote.productName = product?.name;
    }
    if (this.editNote.taggedUserId) {
      const tagged = this.taggableUsers.find((u) => u.id === this.editNote.taggedUserId);
      if (tagged) this.editNote.taggedUserName = tagged.name;
    }

    if (!this.editNote.id) {
      this.editNote.createdByUserName = this.authService.getUsername() ?? undefined;
      this.editNote.dateCreatedUtc = new Date();
    }

    this.editNote.followUpDate = this.editNote.followUpDate
      ? new Date(new Date(this.editNote.followUpDate).toISOString())
      : undefined;

    // Persist any unsaved status update the user typed before saving the note.
    const pendingUpdateSaved = await this.savePendingUpdate();
    if (!pendingUpdateSaved) return;

    this.saved.emit(this.editNote as DriverNote);
  }

  /** True when the Yes answer names at least one of Injuries / Tow away / Fatality. */
  get hasInjuriesDetails(): boolean {
    return (
      this.editNote?.hasInjuries === true ||
      this.editNote?.hasTowAway === true ||
      this.editNote?.hasFatality === true
    );
  }

  /**
   * Answering anything other than Yes clears which-of-the-three, so a "No" note can never save
   * stale flags (the checkboxes are hidden as soon as the answer is not Yes).
   */
  onAnyInjuriesChange(value: boolean): void {
    this.editNote.anyInjuries = value;
    if (value !== true) {
      this.editNote.hasInjuries = undefined;
      this.editNote.hasTowAway = undefined;
      this.editNote.hasFatality = undefined;
    }
  }

  onNoteTypeChange() {
    const currentType = this.editNote.type || "";
    const previousType = this.previousNoteType;

    const wasAccidentOrIncident = this.isAccidentOrIncident(previousType);
    const isAccidentOrIncidentNow = this.isAccidentOrIncident(currentType);
    const wasDelayOrBreakdown = this.isDelayOrBreakdown(previousType);
    const isDelayOrBreakdownNow = this.isDelayOrBreakdown(currentType);

    const formTypeChanged =
      wasAccidentOrIncident !== isAccidentOrIncidentNow ||
      wasDelayOrBreakdown !== isDelayOrBreakdownNow;

    if (formTypeChanged) {
      this.editNote.anyInjuries = undefined;
      this.editNote.hasInjuries = undefined;
      this.editNote.hasTowAway = undefined;
      this.editNote.hasFatality = undefined;
      this.editNote.anotherVehicleInvolved = undefined;
      this.editNote.driverCitation = undefined;
      this.editNote.citationDescription = undefined;
      this.editNote.injuriesDescription = undefined;
      this.editNote.anotherVehicleDescription = undefined;
      this.editNote.tractorId = undefined;
      this.editNote.trailerId = undefined;
      this.editNote.tractorNumber = undefined;
      this.editNote.trailerNumber = undefined;
      this.editNote.shipperTrailerInformation = undefined;
      this.editNote.loadedEmpty = undefined;
      this.editNote.productId = undefined;
      this.editNote.accidentDate = undefined;
      this.editNote.accidentTime = undefined;
      this.editNote.timeZone = undefined;
      this.editNote.accidentCity = undefined;
      this.editNote.accidentState = undefined;
      this.editNote.accidentLocation = undefined;
      this.editNote.weather = undefined;
      this.editNote.accidentDescription = undefined;
      this.editNote.damageDescription = undefined;
      this.editNote.assignedCustomer = undefined;
      this.editNote.photosToSafety = undefined;
      this.editNote.clientId = undefined;
      this.editNote.billToId = undefined;
      this.editNote.tripNumber = undefined;
      this.editNote.hosRemaining = undefined;
      this.editNote.internalNotes = undefined;
      this.editNote.clientNotes = undefined;
      this.editNote.roadCallNumber = undefined;
      this.editNote.customerReferenceNumber = undefined;
      this.editNote.eta = undefined;
      this.etaUnknown = false;
      this.editNote.truckTrailerIssue = undefined;
      this.editNote.startDate = undefined;
      this.editNote.endDate = undefined;
      this.filteredBillTos = [];

      if (isAccidentOrIncidentNow && this.driver) {
        this.editNote.tractorId = this.driver.tractorId;
        this.editNote.trailerId = this.driver.trailerId;
      } else if (isDelayOrBreakdownNow) {
        if (this.isBreakdown(currentType) && this.driver) {
          this.editNote.tractorId = this.driver.tractorId;
          this.editNote.trailerId = this.driver.trailerId;
        }
        const assignedClientId = this.assignedClientId;
        const assignedBillToId = this.assignedBillToId;
        if (assignedClientId || assignedBillToId) {
          this.editNote.clientId = assignedClientId;
          this.editNote.billToId = assignedBillToId;
          if (this.editNote.clientId) {
            this.onClientChange();
          }
        }
        if (!this.editNote.id && !this.editNote.startDate) {
          this.editNote.startDate = new Date();
        }
      }
    }

    this.previousNoteType = currentType;

    // Complaint notes can tag a single internal user; drop the tag for every other type.
    if (!this.isComplaint(currentType)) {
      this.editNote.taggedUserId = undefined;
      this.editNote.taggedUserName = undefined;
    } else if (!this.readOnly) {
      this.loadTaggableUsers();
    }

    // Attachments belong to General notes only; drop them for any other type.
    if (!this.isGeneral(currentType)) {
      this.editNote.attachments = [];
      this.attachmentsTouched = true;
    }
  }

  onClientChange() {
    if (this.editNote.clientId) {
      this.filteredBillTos = this.allClients.filter(
        (c: any) => c.clientId === this.editNote.clientId
      );
    } else {
      this.filteredBillTos = [];
    }
    // Only clear the selected bill-to once the option list has actually loaded,
    // so we don't wipe a saved value while allClients is still loading.
    if (
      this.editNote.billToId &&
      this.allClients.length > 0 &&
      !this.filteredBillTos.some((b: any) => b.id === this.editNote.billToId)
    ) {
      // A consumer may pass a filtered client list (e.g. the dispatch dashboard
      // only keeps active clients) that doesn't contain the driver's assigned
      // bill-to yet. Keep it in that case so the assignment isn't lost while the
      // consumer's list updates.
      const fromDriverAssignment =
        this.editNote.clientId === this.assignedClientId &&
        this.editNote.billToId === this.assignedBillToId;
      if (!fromDriverAssignment) {
        this.editNote.billToId = undefined;
      }
    }
  }

  /**
   * Defaults the client / bill-to to the driver's scheduling team assignment. Only
   * fills values that aren't already set so an existing note or a manual choice is kept.
   */
  private applyCustomerAssignment(): void {
    if (this.editNote.id) return;
    const clientId = this.assignedClientId;
    const billToId = this.assignedBillToId;
    if (clientId && !this.editNote.clientId) {
      this.editNote.clientId = clientId;
    }
    if (billToId && !this.editNote.billToId) {
      this.editNote.billToId = billToId;
    }
    if (this.editNote.clientId) {
      this.onClientChange();
    }
  }

  /**
   * When a driver is picked from the modal's driver dropdown, remember that driver's
   * scheduling team assignment so the client / bill-to can be defaulted.
   */
  onDriverSelectChange(): void {
    if (this.editNote.id) return;

    if (!this.editNote.driverId) {
      // Driver selection cleared - drop the previously reported assignment / team.
      this.driverSelectAssignment = null;
      this.driverSelectTeamMembers = [];
      this.assignmentResolved.emit({});
      return;
    }

    const driverId = this.editNote.driverId.toString();
    this.driverSelectTeamMembers = [];

    // Switching drivers replaces whatever the previous driver's assignment filled in.
    const apply = (
      clientId?: string | null,
      billToId?: string | null,
      teamMembers?: string[] | null
    ) => {
      this.driverSelectAssignment = {
        clientId: clientId ?? undefined,
        billToId: billToId ?? undefined,
      };
      this.driverSelectTeamMembers = teamMembers ?? [];
      this.assignmentResolved.emit(this.driverSelectAssignment);
      if (this.isDelayOrBreakdown(this.editNote.type)) {
        this.editNote.clientId = clientId ?? undefined;
        this.editNote.billToId = billToId ?? undefined;
        this.onClientChange();
      }
      this.cdr.markForCheck();
    };

    const listDriver = this.drivers.find((d: any) => d.id === driverId);
    const listClientId = listDriver?.schedulingTeamClientId;
    const listBillToId = listDriver?.billToClientId;
    const listTeamMembers: string[] | undefined = listDriver?.teamMembers;
    if ((listClientId || listBillToId) && listTeamMembers?.length) {
      apply(listClientId, listBillToId, listTeamMembers);
      return;
    }

    // The dropdown list doesn't carry the assignment / team names, so look the driver up.
    this.driverService.getDriverInfoById(driverId).subscribe({
      next: (driver: DriverViewModel) => {
        if (this.editNote.driverId?.toString() !== driverId) return;
        apply(
          driver?.schedulingTeamClientId ?? listClientId,
          driver?.billToClientId ?? listBillToId,
          driver?.teamMembers ?? listTeamMembers
        );
      },
      error: () => {
        if (listClientId || listBillToId || listTeamMembers?.length) {
          apply(listClientId, listBillToId, listTeamMembers);
        }
      },
    });
  }

  filterWeatherValues(event: any) {
    const query = event.query.toLowerCase();
    this.filteredWeatherValues = this.allWeatherValues.filter((w: string) =>
      w.toLowerCase().includes(query)
    );
    if (query && !this.filteredWeatherValues.some((w: string) => w.toLowerCase() === query)) {
      this.filteredWeatherValues.push(event.query);
    }
  }

  // ── Breakdown status updates ────────────────────────────────────────────────

  private resetUpdateState(): void {
    this.showAddUpdate = false;
    this.isSavingUpdate = false;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {};
    this.editingUpdateId = null;
    this.emailingUpdateId = null;
  }

  /** Keep the timeline oldest-first (ascending by created date). */
  private sortUpdates(): void {
    const updates = this.editNote?.updates;
    if (!Array.isArray(updates)) return;
    updates.sort((a, b) => {
      const at = a.dateCreatedUtc ? new Date(a.dateCreatedUtc as any).getTime() : 0;
      const bt = b.dateCreatedUtc ? new Date(b.dateCreatedUtc as any).getTime() : 0;
      return at - bt;
    });
  }

  /** Ensure an opened note has its updates loaded for the timeline (every note type can have them). */
  private loadNoteUpdates(): void {
    if (!this.editNote?.id) return;
    if (Array.isArray(this.editNote.updates)) return;
    this.driverService.getDriverNote(this.editNote.id).subscribe({
      next: (full: DriverNote) => {
        if (full && Array.isArray(full.updates)) {
          this.editNote.updates = full.updates;
          this.sortUpdates();
          this.cdr.markForCheck();
        }
      },
      error: () => {},
    });
  }

  get hasUpdates(): boolean {
    return Array.isArray(this.editNote.updates) && this.editNote.updates.length > 0;
  }

  openAddUpdate(): void {
    this.editingUpdateId = null;
    this.showAddUpdate = true;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {};
  }

  startEditUpdate(update: DriverNoteUpdate): void {
    this.editingUpdateId = update.id ?? null;
    this.showAddUpdate = true;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {
      label: update.label || "",
      internalNotes: update.internalNotes || "",
      clientNotes: update.clientNotes || "",
    };
  }

  cancelAddUpdate(): void {
    this.showAddUpdate = false;
    this.updateAttemptedSubmit = false;
    this.addUpdate = {};
    this.editingUpdateId = null;
    this.emailingUpdateId = null;
  }

  isAddUpdateValid(): boolean {
    return !!(
      this.addUpdate.label?.trim() ||
      this.addUpdate.internalNotes?.trim() ||
      this.addUpdate.clientNotes?.trim()
    );
  }

  async saveUpdate(): Promise<void> {
    if (!this.editNote.id || this.isSavingUpdate) return;
    this.updateAttemptedSubmit = true;
    if (!this.isAddUpdateValid()) {
      this.messageService.add({ severity: "warn", summary: "Please add notes to the update" });
      return;
    }
    await this.savePendingUpdate();
  }

  /**
   * Persists whatever is currently in the add/edit update form (create or edit).
   * Resolves true when the update was saved (or there was nothing to save), false
   * when the save failed.
   */
  private savePendingUpdate(): Promise<boolean> {
    if (!this.editNote.id || this.isSavingUpdate) return Promise.resolve(true);
    if (!this.showAddUpdate) return Promise.resolve(true);
    if (!this.isAddUpdateValid()) return Promise.resolve(true);

    const noteId = this.editNote.id;
    return new Promise<boolean>((resolve) => {
      const isEditing = !!this.editingUpdateId;
      this.isSavingUpdate = true;
      this.driverService
        .saveDriverNoteUpdate({
          id: isEditing ? (this.editingUpdateId as string) : undefined,
          driverNoteId: noteId,
          label: this.addUpdate.label?.trim() || undefined,
          internalNotes: this.addUpdate.internalNotes?.trim() || undefined,
          clientNotes: this.addUpdate.clientNotes?.trim() || undefined,
        })
        .subscribe({
          next: (saved: any) => {
            this.isSavingUpdate = false;
            const updated = saved as DriverNoteUpdate;
            if (isEditing) {
              const updates = this.editNote.updates || [];
              const idx = updates.findIndex((u) => u.id === updated.id);
              if (idx !== -1) {
                updates[idx] = updated;
              }
              this.messageService.add({ severity: "success", summary: "Update saved" });
            } else {
              this.editNote.updates = this.editNote.updates || [];
              this.editNote.updates.push(updated);
              this.messageService.add({ severity: "success", summary: "Update added" });
            }
            this.sortUpdates();
            this.updateAttemptedSubmit = false;
            this.addUpdate = {};
            this.showAddUpdate = false;
            this.editingUpdateId = null;
            this.cdr.markForCheck();
            resolve(true);
          },
          error: () => {
            this.isSavingUpdate = false;
            this.messageService.add({ severity: "error", summary: "Failed to save update" });
            this.cdr.markForCheck();
            resolve(false);
          },
        });
    });
  }

  deleteUpdate(update: DriverNoteUpdate): void {
    if (!update?.id) return;
    if (!window.confirm("Delete this status update? This cannot be undone.")) return;
    this.driverService.deleteDriverNoteUpdate(update.id).subscribe({
      next: () => {
        this.editNote.updates = (this.editNote.updates || []).filter((u) => u.id !== update.id);
        this.cdr.markForCheck();
        this.messageService.add({ severity: "success", summary: "Update deleted" });
      },
      error: () => {
        this.messageService.add({ severity: "error", summary: "Failed to delete update" });
        this.cdr.markForCheck();
      },
    });
  }

  formatUpdateDate(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  /** Appends the current user's email signature to the bottom of a client-facing email body. */
  private withSenderSignature(body: string): string {
    const signature = this.userEmailSignature?.trim();
    const message = (body ?? "").trim();
    if (!signature) return message;
    return message ? `${message}\n\n${signature}` : signature;
  }

  /**
   * Stores the editable message (without the signature) so the signature editor can
   * re-render it, and returns the body to show/send - message + the sender signature.
   * Every email type goes through here (or buildClientEmailBody) so no email is sent
   * without the user's signature.
   */
  private setEmailMessage(message: string): string {
    this.emailBaseBody = message ?? "";
    return this.withSenderSignature(this.emailBaseBody);
  }

  /** Loads the client emails already sent for the open note, newest first. */
  private loadClientEmailThread(done: () => void): void {
    const noteId = this.editNote?.id;
    if (!noteId) {
      this.noteEmailThread = [];
      done();
      return;
    }
    this.driverService.getDriverNoteEmails(noteId, "toClient").subscribe({
      next: (emails: DriverNoteEmail[]) => {
        this.noteEmailThread = emails || [];
        done();
      },
      error: () => {
        this.noteEmailThread = [];
        done();
      },
    });
  }

  /**
   * Builds the editable new message for a client email (new content + sender
   * signature). The quoted conversation is produced separately as read-only HTML
   * via {@link buildOutlookQuoteHtml} so it can render bold headers and a divider
   * the way Outlook does.
   */
  private buildClientEmailBody(newContent: string): string {
    this.emailBaseBody = newContent;
    const prior = (this.noteEmailThread || [])[0];
    this.emailQuotedHtml = prior && prior.body ? this.buildOutlookQuoteHtml(prior) : "";
    return newContent
      ? this.withSenderSignature(newContent)
      : this.userEmailSignature?.trim() || "";
  }

  /** Formats a prior email as an Outlook-style reply block (bold header + divider). */
  private buildOutlookQuoteHtml(prior: DriverNoteEmail): string {
    const fromName = this.driverNotesFromName?.trim();
    const fromEmail = this.driverNotesFromEmail?.trim();
    const fromAddress =
      fromName && fromEmail ? `${fromName} <${fromEmail}>` : fromName || fromEmail || "";
    const headerRow = (label: string, value: string) =>
      `<div><strong>${label}</strong> ${this.escapeHtml(value)}</div>`;

    const parts: string[] = ["<div>", "<hr />"];
    parts.push(headerRow("From:", fromAddress));
    parts.push(headerRow("Sent:", this.formatOutlookDate(prior.dateCreatedUtc)));
    parts.push(headerRow("To:", prior.toEmails || ""));
    if (prior.ccEmails?.trim()) {
      parts.push(headerRow("Cc:", prior.ccEmails.trim()));
    }
    parts.push(headerRow("Subject:", prior.subject || ""));
    parts.push(`<div>${this.emailHtmlToDisplay(prior.body || "")}</div>`);
    parts.push("</div>");
    return parts.join("");
  }

  /** Prepares stored email HTML for display (older plain-text bodies keep their line breaks). */
  private emailHtmlToDisplay(html: string): string {
    return (html || "").replace(/\r?\n/g, "<br />");
  }

  /** Escapes plain text so it can be embedded safely in the email HTML. */
  private escapeHtml(value: string): string {
    return (value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  /** Builds the HTML sent for a client email: message (escaped, line breaks) + quoted thread. */
  private buildClientSendBody(): string {
    const messageHtml = this.escapeHtml(this.emailPreview.body ?? "").replace(/\r?\n/g, "<br />");
    return this.emailQuotedHtml ? `${messageHtml}<br />${this.emailQuotedHtml}` : messageHtml;
  }

  /** Outlook-style timestamp, e.g. "Wednesday, September 10, 2026 3:45 PM". */
  private formatOutlookDate(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const datePart = d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  }

  /**
   * Prefixes follow-up emails in an existing thread with "RE:" using the
   * thread's root subject, so replies look like a normal email conversation.
   */
  private buildClientEmailSubject(defaultSubject: string): string {
    const thread = this.noteEmailThread || [];
    if (thread.length === 0) return defaultSubject;
    const rootSubject = (thread[thread.length - 1]?.subject || defaultSubject).trim();
    const withoutReplyPrefix = rootSubject.replace(/^(re:\s*)+/i, "").trim();
    return `RE: ${withoutReplyPrefix || defaultSubject}`;
  }

  toggleSignatureEditor(): void {
    this.showSignatureEditor = !this.showSignatureEditor;
    if (this.showSignatureEditor) {
      this.signatureEditText = this.userEmailSignature || "";
      this.signatureSaveMessage = "";
      this.signatureSaveError = false;
    }
  }

  saveSignature(): void {
    if (this.savingSignature) return;
    this.savingSignature = true;
    this.signatureSaveMessage = "";
    this.signatureSaveError = false;
    this.configService.updateCurrentUserEmailSignature(this.signatureEditText ?? "").subscribe({
      next: (saved: string) => {
        this.savingSignature = false;
        this.userEmailSignature = saved || "";
        // Refresh the open email so the signature change shows immediately (any email type).
        this.emailPreview.body = this.withSenderSignature(this.emailBaseBody);
        this.showSignatureEditor = false;
        this.signatureSaveMessage = "";
        this.cdr.markForCheck();
      },
      error: () => {
        this.savingSignature = false;
        this.signatureSaveMessage = "Could not save signature. Please try again.";
        this.signatureSaveError = true;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Emails a completed update to the client. Sending the email marks
   * the update as emailed to the client.
   */
  emailUpdateToClient(update: DriverNoteUpdate): void {
    if (!update?.id) return;
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailUpdateToClient(update));
      return;
    }
    this.openUpdateEmailPreviewFor(update);
  }

  /**
   * Emails a completed update to the driver. Sending the email marks
   * the update as emailed to the driver.
   */
  emailUpdateToDriver(update: DriverNoteUpdate): void {
    if (!update?.id || !this.driver || !this.editNote?.id) return;
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailUpdateToDriver(update));
      return;
    }

    this.emailingUpdateId = update.id;
    this.emailQuotedHtml = "";
    const tripNumber = (this.editNote.tripNumber ?? "").toString().trim();
    const publicNotes = (update.clientNotes || "").trim();
    const label = (update.label || "").trim();

    const subjectParts = [
      `${this.editNote.type} update`,
      label,
      tripNumber ? `Trip ${tripNumber}` : "",
    ].filter((part) => !!part);

    // The message is JUST the update's public notes - no greeting or boilerplate copy.
    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: subjectParts.join(" - "),
      body: this.setEmailMessage(publicNotes),
    };
    this.currentEmailType = "toDriver";
    this.showEmailPreview = true;
    this.cdr.markForCheck();
  }

  /**
   * Opens the Review-Email dialog for a note update. Sending that email
   * marks the update as emailed to the client.
   */
  private openUpdateEmailPreviewFor(update: DriverNoteUpdate): void {
    this.emailingUpdateId = update?.id ?? null;

    const finish = (clientEmail: string) => {
      const publicNotes = (update.clientNotes || "").trim();
      this.emailPreview = {
        to: clientEmail,
        ccEmails: this.driverNotesCcEmail,
        subject: this.buildClientEmailSubject(`${this.editNote.type} update`),
        body: this.buildClientEmailBody(publicNotes),
      };
      this.currentEmailType = "toClient";
      this.showEmailPreview = true;
      this.cdr.markForCheck();
    };

    // Recipients default to the driver's scheduling team contacts (not the client/bill-to email)
    this.driverService
      .getClientEmailRecipients(this.driver, this.editNote.driverId)
      .subscribe((to) => this.loadClientEmailThread(() => finish(to)));
  }

  // Email methods
  emailToSafety() {
    if (!this.editNote?.id || !this.driver) return;
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailToSafety());
      return;
    }
    this.emailingUpdateId = null;
    this.emailQuotedHtml = "";
    const driverName = this.driverEmailName;
    const noteDetails = this.buildAccidentNoteDetails(this.editNote as DriverNote);
    this.emailPreview = {
      to: "safety@lgttransport.com",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Report - Driver ${driverName}`,
      body: this.setEmailMessage(`Driver: ${driverName}\n\n${noteDetails}`),
    };
    this.currentEmailType = "toSafety";
    this.showEmailPreview = true;
  }

  emailFormToDriver() {
    if (!this.editNote?.id || !this.driver) return;
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailFormToDriver());
      return;
    }
    this.emailingUpdateId = null;
    this.emailQuotedHtml = "";
    const driverName = this.driverEmailName;
    const formLink =
      "https://docs.google.com/forms/d/e/1FAIpQLSecnRaMbaTlvEyjgeHzM2Qo4tDFfFAjNisgxSDpl8K4SfPPsw/viewform";
    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Form Required - ${driverName}`,
      body: this.setEmailMessage(
        `${driverName},\n\nPlease use the link below to fill out the LGT Transport Accident Review Form.\n\n${formLink}\n\nAlso, please send any photos or documents to the following email: Safety@lgttransport.com\n\nThank you`
      ),
    };
    this.currentEmailType = "formToDriver";
    this.showEmailPreview = true;
  }

  emailToClient() {
    if (!this.editNote?.id || !this.driver) return;
    // Never compose a note email before the notes CC is known, so it can't go out without it.
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailToClient());
      return;
    }

    // Recipients default to the driver's scheduling team contacts (not the client/bill-to email)
    this.driverService
      .getClientEmailRecipients(this.driver, this.editNote.driverId)
      .subscribe((to) => {
        this.openEmailToClientPreview(to);
        this.cdr.markForCheck();
      });
  }

  private openEmailToClientPreview(clientEmail: string) {
    this.emailingUpdateId = null;
    const driverName = this.driverEmailName;
    const tripNumber = (this.editNote.tripNumber ?? "").toString().trim();

    // A driver release relays the release details rather than the delay/breakdown notice.
    if (this.isDriverRelease(this.editNote.type)) {
      this.openEmailToClientPreviewForRelease(clientEmail);
      return;
    }

    // General notes are free text, so the client email simply relays the note
    // instead of the structured delay/breakdown notification.
    if (this.isGeneral(this.editNote.type)) {
      const generalDetails = this.buildGeneralNoteDetails(this.editNote as DriverNote);
      this.loadClientEmailThread(() => {
        const body = `We wanted to share the following note with you:\n\nDriver: ${driverName}${
          generalDetails ? `\n${generalDetails}` : ""
        }`;
        const subjectParts = [
          "General Note",
          driverName,
          tripNumber ? `Trip ${tripNumber}` : "",
        ].filter((part) => !!part);
        this.emailPreview = {
          to: clientEmail,
          ccEmails: this.driverNotesCcEmail,
          subject: this.buildClientEmailSubject(subjectParts.join(" - ")),
          body: this.buildClientEmailBody(body),
        };
        this.currentEmailType = "toClient";
        this.showEmailPreview = true;
        this.cdr.markForCheck();
      });
      return;
    }

    const delayDetails = this.buildDelayNoteDetails(this.editNote as DriverNote);
    const noteTypeForMessage = this.editNote.type?.toLowerCase().includes("delay")
      ? "delay"
      : "breakdown";
    const noteTypeLabel = noteTypeForMessage === "delay" ? "Delay" : "Breakdown";
    this.loadClientEmailThread(() => {
      // The trip number comes from the details block below, so it isn't repeated here.
      const lines = [`Driver: ${driverName}`, delayDetails].filter((line) => !!line);
      const body = `We wanted to inform you of a ${noteTypeForMessage}:\n\n${lines.join(
        "\n"
      )}\n\nThank you for your understanding.`;
      const subjectParts = [
        `${noteTypeLabel} Notification`,
        driverName,
        tripNumber ? `Trip ${tripNumber}` : "",
      ].filter((part) => !!part);
      this.emailPreview = {
        to: clientEmail,
        ccEmails: this.driverNotesCcEmail,
        subject: this.buildClientEmailSubject(subjectParts.join(" - ")),
        body: this.buildClientEmailBody(body),
      };
      this.currentEmailType = "toClient";
      this.showEmailPreview = true;
      this.cdr.markForCheck();
    });
  }

  /**
   * Emails a driver release's details to the customer (the driver's scheduling team contacts),
   * copied to the notes CC address - the same shape as the General note email.
   */
  private openEmailToClientPreviewForRelease(clientEmail: string) {
    this.emailingUpdateId = null;
    const driverName = this.driverEmailName;
    const tripNumber = (this.editNote.tripNumber ?? "").toString().trim();
    const releaseDetails = this.buildDriverReleaseNoteDetails(this.editNote as DriverNote);
    const subjectParts = [
      "Driver Release",
      driverName,
      tripNumber ? `Trip ${tripNumber}` : "",
    ].filter((part) => !!part);

    this.loadClientEmailThread(() => {
      const body = `We wanted to share the following note with you:\n\nDriver: ${driverName}${
        releaseDetails ? `\n${releaseDetails}` : ""
      }`;
      this.emailPreview = {
        to: clientEmail,
        ccEmails: this.driverNotesCcEmail,
        subject: this.buildClientEmailSubject(subjectParts.join(" - ")),
        body: this.buildClientEmailBody(body),
      };
      this.currentEmailType = "toClient";
      this.showEmailPreview = true;
      this.cdr.markForCheck();
    });
  }

  emailToDriver() {
    if (!this.editNote?.id || !this.driver) return;
    if (!this.driverNotesCcEmail) {
      this.ensureCcEmail(() => this.emailToDriver());
      return;
    }
    this.emailingUpdateId = null;
    this.emailQuotedHtml = "";
    const driverName = this.driverEmailName;

    // A driver release has no customer-facing narrative, so the email simply relays the
    // release details the dispatcher filled in (same shape as the General note email).
    if (this.isDriverRelease(this.editNote.type)) {
      const releaseDetails = this.buildDriverReleaseNoteDetails(this.editNote as DriverNote);
      const tripNumber = (this.editNote.tripNumber ?? "").toString().trim();
      const subjectParts = ["Driver Release", tripNumber ? `Trip ${tripNumber}` : ""].filter(
        (part) => !!part
      );
      const lines = [
        releaseDetails,
        "Please contact dispatch if you have any questions.",
        "Thank you.",
      ].filter((line) => !!line);
      this.emailPreview = {
        to: this.driver.email || "",
        ccEmails: this.driverNotesCcEmail,
        subject: subjectParts.join(" - "),
        body: this.setEmailMessage(`${driverName},\n\n${lines.join("\n\n")}`),
      };
      this.currentEmailType = "toDriver";
      this.showEmailPreview = true;
      return;
    }

    // General notes are free text, so the driver email simply relays the note
    // instead of the structured delay/breakdown notification.
    if (this.isGeneral(this.editNote.type)) {
      const generalDetails = this.buildGeneralNoteDetails(this.editNote as DriverNote);
      const tripNumber = (this.editNote.tripNumber ?? "").toString().trim();
      const subjectParts = ["General Note", tripNumber ? `Trip ${tripNumber}` : ""].filter(
        (part) => !!part
      );
      const lines = [
        generalDetails,
        "Please contact dispatch if you have any questions.",
        "Thank you.",
      ].filter((line) => !!line);
      this.emailPreview = {
        to: this.driver.email || "",
        ccEmails: this.driverNotesCcEmail,
        subject: subjectParts.join(" - "),
        body: this.setEmailMessage(`${driverName},\n\n${lines.join("\n\n")}`),
      };
      this.currentEmailType = "toDriver";
      this.showEmailPreview = true;
      return;
    }

    const delayDetails = this.buildDelayNoteDetails(this.editNote as DriverNote);
    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Notification - Trip ${this.editNote.tripNumber || "N/A"}`,
      body: this.setEmailMessage(
        `${driverName},\n\nThis is a notification regarding your current trip:\n\n${delayDetails}\n\nPlease contact dispatch if you have any questions.\n\nThank you.`
      ),
    };
    this.currentEmailType = "toDriver";
    this.showEmailPreview = true;
  }

  // ── Post-create email prompt ──────────────────────────────────────────────

  /** Note types that have something to email once the note has been created. */
  canPromptEmail(type?: string): boolean {
    return this.isAccidentOrIncident(type) || this.isDelayOrBreakdown(type) || this.isGeneral(type);
  }

  /**
   * Hosts call this once they have persisted a newly created note so the user is asked
   * whether to email the client / driver (the same prompt the driver notes tab shows).
   * The driver is optional: callers that only have a lightweight driver object can pass
   * the full driver they loaded for the note. Returns true when the prompt was shown.
   */
  promptEmailForSavedNote(savedNote: DriverNote, driver?: DriverViewModel | null): boolean {
    if (!savedNote?.id || !this.canPromptEmail(savedNote.type)) return false;

    if (driver) this.driver = driver;
    // Adopt the persisted note (its id / email tracking) so the email actions work.
    this.editNote = { ...this.editNote, ...savedNote };
    this.promptNoteType = savedNote.type || "";
    this.showEmailPrompt = true;
    this.cdr.markForCheck();
    return true;
  }

  closeEmailPrompt(): void {
    this.showEmailPrompt = false;
    this.promptNoteType = "";
    this.cdr.markForCheck();
  }

  /** Prompt actions: close the prompt first so only the Review Email dialog is on top. */
  emailSafetyFromPrompt(): void {
    this.closeEmailPrompt();
    this.emailToSafety();
  }

  emailFormFromPrompt(): void {
    this.closeEmailPrompt();
    this.emailFormToDriver();
  }

  emailClientFromPrompt(): void {
    this.closeEmailPrompt();
    this.emailToClient();
  }

  emailDriverFromPrompt(): void {
    this.closeEmailPrompt();
    this.emailToDriver();
  }

  confirmSendEmail() {
    if (!this.validateEmailFields()) return;
    if (!this.editNote?.id) return;

    const sendFn = (emailType: string) => {
      const updateId = this.emailingUpdateId ?? undefined;
      const sendBody =
        emailType === "toClient" ? this.buildClientSendBody() : this.emailPreview.body;
      this.driverService
        .sendCustomEmail(
          this.editNote.id!,
          this.emailPreview.to,
          this.emailPreview.subject,
          sendBody,
          emailType,
          this.emailPreview.ccEmails,
          this.emailAttachments,
          updateId
        )
        .subscribe(
          (response: any) => {
            this.showEmailPreview = false;
            this.emailAttachments = [];
            const existingUpdates = this.editNote.updates;
            if (response.note) {
              Object.assign(this.editNote, response.note);
            }

            if (!Array.isArray(this.editNote.updates)) {
              this.editNote.updates = existingUpdates;
              this.sortUpdates();
            }
            if (response.update) {
              const existing = (this.editNote.updates || []).find(
                (u) => u.id === response.update.id
              );
              if (existing) {
                Object.assign(existing, response.update);
              }
            }
            this.emailingUpdateId = null;
            if (updateId) {
              // The email was for a just-added update; the add form is already closed.
              this.messageService.add({
                severity: "success",
                summary: "Update emailed",
                detail: `Update sent to ${this.emailPreview.to}`,
              });
            } else {
              this.messageService.add({
                severity: "success",
                summary: "Email Sent",
                detail: `Email has been sent to ${this.emailPreview.to}`,
              });
            }
            this.cdr.markForCheck();
          },
          (error: any) => {
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: error.error?.error || "Failed to send email. Please try again.",
            });
            this.cdr.markForCheck();
          }
        );
    };

    switch (this.currentEmailType) {
      case "toSafety":
        sendFn("toSafety");
        break;
      case "formToDriver":
        sendFn("formToDriver");
        break;
      case "toClient":
        sendFn("toClient");
        break;
      case "toDriver":
        sendFn("toDriver");
        break;
    }
  }

  cancelEmailPreview() {
    this.showEmailPreview = false;
    this.currentEmailType = null;
    this.emailValidationErrors = { to: "", cc: "" };
    this.emailAttachments = [];
    this.emailingUpdateId = null;
    this.emailQuotedHtml = "";
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (file.type.startsWith("image/")) {
          this.emailAttachments.push(file);
        }
      }
    }
    input.value = "";
  }

  removeAttachment(index: number) {
    this.emailAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  clearEmailValidationError(field: "to" | "cc") {
    this.emailValidationErrors[field] = "";
  }

  isValidEmail(email: string): boolean {
    if (!email || !email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  validateEmailFields(): boolean {
    this.emailValidationErrors = { to: "", cc: "" };
    let isValid = true;
    if (!this.emailPreview.to || !this.emailPreview.to.trim()) {
      this.emailValidationErrors.to = "Recipient email is required.";
      isValid = false;
    } else {
      // 'to' may hold several addresses separated by semicolons (or commas)
      const toEmails = this.emailPreview.to
        .split(/[;,]/)
        .map((e: string) => e.trim())
        .filter((e: string) => e);
      for (const email of toEmails) {
        if (!this.isValidEmail(email)) {
          this.emailValidationErrors.to = `Invalid email: ${email}`;
          isValid = false;
          break;
        }
      }
    }
    if (this.emailPreview.ccEmails && this.emailPreview.ccEmails.trim()) {
      const ccEmails = this.emailPreview.ccEmails
        .split(/[;,]/)
        .map((e: string) => e.trim())
        .filter((e: string) => e);
      for (const cc of ccEmails) {
        if (!this.isValidEmail(cc)) {
          this.emailValidationErrors.cc = `Invalid email: ${cc}`;
          isValid = false;
          break;
        }
      }
    }
    return isValid;
  }

  /** Formats a product name in title case while preserving chemical formulas (CO2, NH3) and short acronyms (LPG, CNG). */
  private formatProductName(name: string): string {
    if (!name) return name;
    return name
      .split(/\s+/)
      .map((word) => {
        if (/\d/.test(word)) return word; // CO2, NH3, etc.
        if (word.length <= 4 && word === word.toUpperCase()) return word; // LPG, CNG, O2, etc.
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }

  /** Unit number for a tractor id, from the host-supplied list (the number itself is only mapped server-side). */
  private tractorNumberForId(tractorId?: string | null): string | undefined {
    if (!tractorId) return undefined;
    const tractor = this.tractors.find((t: any) => t.id === tractorId);
    const number = tractor?.unitNumber || tractor?.itemName;
    return number ? String(number) : undefined;
  }

  /** Trailer number for a trailer id, from the host-supplied list. */
  private trailerNumberForId(trailerId?: string | null): string | undefined {
    if (!trailerId) return undefined;
    const trailer = this.trailers.find((t: any) => t.id === trailerId);
    const number = trailer?.trailerNo;
    return number ? String(number) : undefined;
  }

  /** Product name for a product id, from the host-supplied list. */
  private productNameForId(productId?: string | null): string | undefined {
    if (!productId) return undefined;
    const product = this.products.find((p: any) => p.id === productId);
    const name = product?.name;
    return name ? String(name) : undefined;
  }

  buildAccidentNoteDetails(note: DriverNote): string {
    let details = [];

    // Safety acts on these three first, so they lead the email instead of sitting under the
    // narrative fields where they used to be buried.
    if (note.anyInjuries !== undefined) {
      // Name which of the three apply, so the email says more than a bare "YES".
      const specifics = [
        note.hasInjuries ? "Injuries" : "",
        note.hasTowAway ? "Tow away" : "",
        note.hasFatality ? "Fatality" : "",
      ]
        .filter(Boolean)
        .join(", ");
      details.push(
        `Injuries/Tow-aways/Fatalities: ${note.anyInjuries ? "YES" : "NO"}${
          specifics ? ` - ${specifics}` : ""
        }`
      );
    }
    if (note.anyInjuries && note.injuriesDescription)
      details.push(`Injuries/Tow-aways/Fatalities Details: ${note.injuriesDescription}`);
    if (note.anotherVehicleInvolved !== undefined)
      details.push(`Another Vehicle: ${note.anotherVehicleInvolved ? "YES" : "NO"}`);
    if (note.anotherVehicleInvolved && note.anotherVehicleDescription)
      details.push(`Another Vehicle Details: ${note.anotherVehicleDescription}`);
    if (note.driverCitation !== undefined)
      details.push(`Citation: ${note.driverCitation ? "YES" : "NO"}`);
    if (note.citationDescription) details.push(`Citation Details: ${note.citationDescription}`);

    // The unit numbers are mapped server-side, so fall back to the id lookups - otherwise a note
    // that only carries ids (e.g. right after it was created) emailed without truck/trailer info.
    const tractorNumber = note.tractorNumber || this.tractorNumberForId(note.tractorId);
    const trailerNumber = note.trailerNumber || this.trailerNumberForId(note.trailerId);
    if (tractorNumber) details.push(`Tractor: ${tractorNumber}`);
    if (trailerNumber) details.push(`Trailer: ${trailerNumber}`);
    if (note.shipperTrailerInformation)
      details.push(`Shipper Trailer Info: ${note.shipperTrailerInformation}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty}`);
    if (note.productName) details.push(`Product: ${this.formatProductName(note.productName)}`);
    if (note.accidentDate) details.push(`Date: ${note.accidentDate}`);
    if (note.accidentTime) details.push(`Time: ${note.accidentTime}`);
    if (note.timeZone) details.push(`Time Zone: ${note.timeZone}`);
    if (note.accidentCity || note.accidentState)
      details.push(`Location: ${note.accidentCity || ""}, ${note.accidentState || ""}`);
    if (note.weather) details.push(`Weather: ${note.weather}`);
    if (note.accidentLocation) details.push(`Accident Location: ${note.accidentLocation}`);
    if (note.accidentDescription) details.push(`Description: ${note.accidentDescription}`);
    if (note.damageDescription) details.push(`Damage: ${note.damageDescription}`);
    if (note.assignedCustomer) details.push(`Assigned Customer: ${note.assignedCustomer}`);
    if (note.photosToSafety) details.push(`Photos to Safety: ${note.photosToSafety}`);
    if (note.note) details.push(`Notes: ${note.note}`);
    return details.join("\n");
  }

  buildDelayNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tripNumber) details.push(`Trip: ${note.tripNumber}`);
    if (note.customerReferenceNumber)
      details.push(`Customer Reference: ${note.customerReferenceNumber}`);
    if (note.eta) details.push(`ETA: ${this.formatDateTime(note.eta)}`);
    if (note.truckTrailerIssue) details.push(`Issue Type: ${note.truckTrailerIssue}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty}`);
    if (note.productName) details.push(`Product: ${this.formatProductName(note.productName)}`);
    if (note.clientNotes) details.push(`Notes: ${note.clientNotes}`);
    return details.join("\n");
  }

  /**
   * Driver release details for the dispatch email: where the driver was released, what they
   * were carrying and when they are available again.
   */
  buildDriverReleaseNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tripNumber) details.push(`Trip: ${note.tripNumber}`);
    if (note.customerReferenceNumber)
      details.push(`Customer Reference: ${note.customerReferenceNumber}`);
    if (note.driverLocation) details.push(`Location: ${note.driverLocation}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty}`);
    // The name is mapped server-side, so fall back to the host's product list for a note that
    // only carries the id (the modal is filled from the create flow before it is reloaded).
    const productName = note.productName || this.productNameForId(note.productId);
    if (productName) details.push(`Product: ${this.formatProductName(productName)}`);
    if (note.trailerDropoffLocation)
      details.push(`Trailer Dropoff Location: ${note.trailerDropoffLocation}`);
    if (note.nextAvailable) details.push(`Next Available: ${note.nextAvailable}`);
    if (note.requestTimeOff !== undefined && note.requestTimeOff !== null)
      details.push(`Requesting Time Off: ${note.requestTimeOff ? "YES" : "NO"}`);
    if (note.note) details.push(`Notes: ${note.note}`);
    return details.join("\n");
  }

  /**
   * General notes only capture free text (plus any shared trip fields that were
   * populated), so the client email relays whichever of those are set.
   */
  buildGeneralNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tripNumber) details.push(`Trip: ${note.tripNumber}`);
    if (note.customerReferenceNumber)
      details.push(`Customer Reference: ${note.customerReferenceNumber}`);
    if (note.note) details.push(`Notes: ${note.note}`);
    return details.join("\n");
  }

  isAccidentOrIncident(type?: string): boolean {
    return type === "Accident" || type === "Incident";
  }

  isDelayOrBreakdown(type?: string): boolean {
    return type === "Delays" || type === "Breakdown & follow up";
  }

  isGeneral(type?: string): boolean {
    return type === "General";
  }

  isComplaint(type?: string): boolean {
    return type === "Complaint";
  }

  /** Note types whose details can be emailed to the customer. */
  canEmailClient(type?: string): boolean {
    return this.isDelayOrBreakdown(type) || this.isGeneral(type) || this.isDriverRelease(type);
  }

  /** Note types whose details can be emailed to the driver. */
  canEmailDriver(type?: string): boolean {
    return this.isDelayOrBreakdown(type) || this.isGeneral(type) || this.isDriverRelease(type);
  }

  isBreakdown(type?: string): boolean {
    return type === "Breakdown & follow up";
  }

  isDriverRelease(type?: string): boolean {
    return type === "Driver Release";
  }

  private formatDateTime(date: Date): string {
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  private scrollToFirstError() {
    setTimeout(() => {
      const modalBody = document.querySelector(".note-form-dialog .modal-body");
      if (!modalBody) return;
      const firstError = modalBody.querySelector(".is-invalid, .form-control.ng-invalid");
      if (firstError) {
        const errorPosition = (firstError as HTMLElement).offsetTop;
        modalBody.scrollTo({ top: errorPosition - 20, behavior: "smooth" });
        if (
          firstError instanceof HTMLElement &&
          (firstError.tagName === "INPUT" ||
            firstError.tagName === "SELECT" ||
            firstError.tagName === "TEXTAREA")
        ) {
          firstError.focus();
        }
      }
    }, 100);
  }
}
